import { Capacitor } from "@capacitor/core";
import { utenteInterno } from "@/lib/marchi";
import { GRATUITO_DOMANDE_SETTIMANA, PROVA_GIORNI, TESSERA_GRATUITA } from "@/lib/offerta";

/**
 * SULL'APP PER IPHONE: ISCRIZIONE SÌ, INVITI A COMPRARE NO (Apple 3.1.1).
 *
 * Il 29/09/2026 Apple ha respinto la build 8: «The app includes an account
 * registration feature for businesses and organizations, which is considered
 * access to external mechanisms for purchases». Il punto non era l'iscrizione:
 * era il muro dopo i 7 giorni, con «richiedi l'attivazione a Datacom» e i tasti
 * che portavano alla richiesta. Per Apple quello è un invito a comprare fuori.
 *
 * Decisione di Davide (29/09): l'iscrizione resta nell'app anche su iPhone, e
 * crea un account GRATUITO che non scade (gli strumenti restano sempre liberi,
 * come le app «gratis + abbonamento sul sito»: Spotify, Slack). Dentro l'app
 * iOS però nessun prezzo, nessun tasto o testo che porti a richiedere o
 * comprare l'account installatore: la licenza si propone FUORI dall'app (mail
 * di benvenuto e del 5° giorno, Datacom al telefono), che Apple consente.
 *
 * Il sito e l'app Android restano come sono.
 */
export function invitiAllAcquistoNellApp(): boolean {
  return Capacitor.getPlatform() !== "ios";
}

/**
 * SULL'IPHONE SOLO L'ACCESSO, NIENTE ISCRIZIONE (05/10/2026).
 *
 * Quarto rifiuto di Apple (build 127, rivista il 05/10 su iPad), sempre 3.1.1 e
 * sempre la stessa frase: «The app still includes an account registration
 * feature for businesses and organizations… Remove the account registration
 * features». Non bastava piu' togliere i dati d'impresa e la prova a tempo:
 * per Apple qualunque iscrizione in un'app per professionisti e' la
 * registrazione di un'azienda. Decisione di Davide (05/10): sull'iPhone resta
 * solo «Accedi» con email e password; spariscono «Crea account» e gli accessi
 * con Apple e Google (che creano l'account al primo accesso). Gli strumenti di
 * calcolo restano liberi anche senza account. Gli account nascono dal sito e
 * dall'app Android, e nell'app iPhone non lo si dice (sarebbe un invito fuori).
 */
export function registrazioneNellApp(): boolean {
  return Capacitor.getPlatform() !== "ios";
}

/** Testi neutri per l'iPhone, al posto di quelli che mandano a Datacom per l'attivazione. */
export const TESTI_IOS = {
  valutazioneFinita:
    "L'assistente non è disponibile per il tuo account in questo momento. Gli strumenti di calcolo restano a tua disposizione.",
  accessoNonAttivo:
    "L'assistente non è attivo per il tuo account in questo momento. Gli strumenti di calcolo restano a tua disposizione.",
  quotaSettimana: "Hai raggiunto le domande di questa settimana: il conteggio riparte lunedì.",
  // 23/10/2026: niente «valutazione» sull'iPhone (offerta unica, docs/offerta-unica/FRASARIO.md).
  quotaValutazione: "Hai usato le domande a tua disposizione per ora. Gli strumenti di calcolo restano a tua disposizione.",
} as const;

/**
 * L'ACCOUNT GRATUITO DELL'IPHONE (03/10/2026, dopo il terzo rifiuto di Apple:
 * lo screenshot del revisore era proprio il modulo d'iscrizione con Azienda e
 * Partita IVA e i «primi 7 giorni»). Decisione di Davide: l'iscrizione resta,
 * ma sull'iPhone crea un account gratuito che non scade - strumenti di calcolo
 * e 3 domande a settimana sulle norme - senza dati d'impresa e senza prova a
 * tempo. La licenza non si nomina mai nell'app; chi ce l'ha entra e la usa.
 * Il server riconosce l'iPhone dall'intestazione x-piattaforma (chatClient).
 */
export function datiImpresaNellIscrizione(): boolean {
  return Capacitor.getPlatform() !== "ios";
}

export const DOMANDE_GRATUITE_SETTIMANA = GRATUITO_DOMANDE_SETTIMANA;

/** La tessera dell'account sull'iPhone, per chi non ha la licenza. */
export const TESSERA_GRATUITA_IOS = TESSERA_GRATUITA;

/**
 * La risposta del motore sui marchi fuori dall'accesso arriva gia' scritta dal
 * server (Dify, nodo codice), che non sa da che piattaforma parte la domanda:
 * «X non è incluso nel tuo abbonamento… Per aggiungere un marchio, scrivi a
 * Datacom.» Sull'iPhone si toglie l'invito e la parola «abbonamento» (01/10).
 */
export function rispostaPerQuestaApp(testo: string): string {
  // Offerta unica (23/10/2026): «abbonamento» non e' piu' una parola dell'app,
  // nemmeno sul sito; chi non ha la licenza ha l'account gratuito.
  const pulito = testo.replace(/nel tuo abbonamento/gi, "nel tuo account");
  if (invitiAllAcquistoNellApp()) return pulito;
  return pulito.replace(/\s*Per aggiungere (?:un|altri) marc\w*,? scrivi a Datacom\.?/gi, "");
}

/**
 * La dichiarazione di conformita' e' della licenza (Davide, 03/10): sull'iPhone
 * l'account gratuito non la vede. Ce l'ha chi ha marchi validi, chi e' ancora
 * nei 7 giorni con la partita IVA (account nato sul sito) e l'admin. Fuori
 * dall'iPhone decide la pagina come prima (blocco a valutazione finita).
 */
type ProfiloLicenza = { brands?: string[] | null; scadenza?: string | null; created_at?: string | null; partita_iva?: string | null };

/** Ha marchi e la scadenza non e' passata: e' un account con licenza. */
export function licenzaValida(profile: ProfiloLicenza | null): boolean {
  if (!profile) return false;
  const scad = profile.scadenza ? Date.parse(profile.scadenza) : null;
  return (profile.brands ?? []).length > 0 && (scad === null || Number.isNaN(scad) || scad > Date.now());
}

export function dicoNellApp(profile: ProfiloLicenza | null, isAdmin = false): boolean {
  if (Capacitor.getPlatform() !== "ios") return true;
  if (isAdmin) return true;
  if (!profile) return false;
  if (licenzaValida(profile)) return true;
  const nato = Date.parse(profile.created_at ?? "");
  const inProva = !Number.isNaN(nato) && Date.now() - nato < PROVA_GIORNI * 86_400_000;
  return inProva && Boolean(profile.partita_iva);
}

/**
 * LA CHAT NON SI BLOCCA DALL'APP SULL'IPHONE (04/10/2026, audit). Il client
 * calcola «scaduto» per chi non ha licenza da piu' di 7 giorni, e fino alla
 * 126 su iPhone spegneva il campo e apriva «periodo di valutazione terminato»
 * - mentre il server da' a quell'account le 3 domande gratuite. Sull'iPhone il
 * limite lo mette solo il server (429 «domande di questa settimana»).
 */
export function chatBloccataQui(_stato: { tipo: string } | null | undefined): boolean {
  // OFFERTA UNICA (23/10/2026, proposta C di Davide): finita la prova l'account
  // resta gratuito ovunque, non piu' solo sull'iPhone. L'app non chiude mai il
  // campo da sola: il limite lo mette il server (429 «domande di questa settimana»).
  return false;
}

/**
 * Esempi per chi sull'iPhone ha l'account gratuito: solo norme e prassi, che
 * e' cio' a cui il gratuito risponde. Proporre domande sui marchi gli faceva
 * spendere le 3 domande per sentirsi dire «non incluso».
 */
export const ESEMPI_NORME = [
  "Quali norme cito nella dichiarazione di conformità per un impianto antintrusione?",
  "Qual è l'edizione in vigore della UNI 9795?",
  "Come dimensiono la batteria tampone per 24 ore di autonomia?",
] as const;

export function soloEsempiNorme(profile: ProfiloLicenza | null): boolean {
  if (!profile || licenzaValida(profile)) return false;
  if (Capacitor.getPlatform() === "ios") return true;
  // Sito e Android (23/10/2026): finita la prova si ha l'account gratuito, che
  // risponde solo sulle norme.
  const nato = Date.parse(profile.created_at ?? "");
  return !Number.isNaN(nato) && Date.now() - nato >= PROVA_GIORNI * 86_400_000;
}

/**
 * CONSENSO PER L'INTELLIGENZA ARTIFICIALE DI TERZI (Apple 5.1.2(i), 04/10/2026).
 * Domande, foto e voce vanno a OpenAI e ad Anthropic: sull'iPhone si chiede un
 * permesso esplicito una volta, prima del primo uso dell'assistente. Chi si
 * iscrive con Google o Apple non spunta nessuna casella, quindi la casella del
 * modulo non basta. Il permesso resta sul telefono, per account.
 *
 * 14/10/2026: la chiave ha preso il «v2.» quando fra chi riceve i dati e'
 * entrata Anthropic. Chi aveva detto si' a OpenAI soltanto non ha detto si' ad
 * Anthropic: alla prima apertura con la build nuova il permesso si richiede.
 */
export function consensoAiRichiesto(): boolean {
  return Capacitor.getPlatform() === "ios";
}

const CHIAVE_CONSENSO_AI = "datacom.consenso_ai.v2.";

export function consensoAiDato(userId: string | null | undefined): boolean {
  if (!consensoAiRichiesto()) return true;
  if (!userId) return false;
  try {
    return window.localStorage.getItem(CHIAVE_CONSENSO_AI + userId) === "si";
  } catch {
    return false;
  }
}

export function registraConsensoAi(userId: string | null | undefined): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(CHIAVE_CONSENSO_AI + userId, "si");
  } catch {
    /* memoria non disponibile: il permesso verra' richiesto di nuovo */
  }
}

/**
 * I DATI CHE MANCANO, E CHI LI DEVE SCRIVERE (09/10/2026).
 *
 * Umberto crea l'account dal pannello partendo dall'email della persona, e
 * scrive solo quello che sa (Davide, 09/10: «immette gli ultimi dati e poi
 * puo' iniziare ad usare l'app»). Al primo accesso, dopo la password, l'app
 * chiede quello che manca - e solo quello: nome, cognome, impresa, partita IVA,
 * telefono. Per chi e' entrato da solo (modulo, Google, Apple) la regola resta
 * quella di sempre: senza partita IVA si completa il profilo (impresa, partita
 * IVA e telefono). Gli account interni iscritti da Datacom completano solo il
 * nome; quelli di prova dei costruttori niente.
 */
export type CampoProfilo = "nome" | "cognome" | "impresa" | "partita_iva" | "telefono";

type ProfiloCompleto = {
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  partita_iva?: string | null;
  telefono?: string | null;
  account_prova?: boolean | null;
};
type UtenteIscritto = { email?: string | null; user_metadata?: Record<string, unknown> | null } | null | undefined;

/** L'account l'ha creato un admin dal pannello (crea-installatore scrive chi). */
export function iscrittoDaDatacom(utente: UtenteIscritto): boolean {
  const da = utente?.user_metadata?.iscritto_da;
  return typeof da === "string" && da.length > 0;
}

/**
 * DOVE SI COMPLETA IL PROFILO (02/11/2026, Davide: «non gli fa inserire la
 * partita IVA… la devono inserire gli utenti»).
 *
 * Sull'iPhone i dati d'impresa non si chiedono (account gratuito, 03/10). Ma
 * chi e' stato iscritto da Datacom dal pannello non si e' registrato
 * nell'app: l'account esiste gia', e dal 02/11 la mail di benvenuto lo manda
 * prima di tutto nell'app. Senza la partita IVA il server gli da' l'account
 * gratuito (accesso.ts) e perde la licenza che Datacom gli ha dato. Quindi lui
 * completa i dati anche sull'iPhone. Il revisore di Apple non lo vede: il suo
 * account e' interno, e agli interni la partita IVA non si chiede.
 */
export function profiloDaCompletareQui(utente: UtenteIscritto): boolean {
  return datiImpresaNellIscrizione() || iscrittoDaDatacom(utente);
}

export function campiDaCompletare(profile: ProfiloCompleto | null | undefined, utente: UtenteIscritto): CampoProfilo[] {
  if (!profile || profile.account_prova) return [];
  const vuoto = (s: string | null | undefined) => !(s ?? "").trim();
  if (!iscrittoDaDatacom(utente)) {
    return vuoto(profile.partita_iva) ? ["impresa", "partita_iva", "telefono"] : [];
  }
  const campi: CampoProfilo[] = [];
  if (vuoto(profile.first_name)) campi.push("nome");
  if (vuoto(profile.last_name)) campi.push("cognome");
  if (!utenteInterno(utente?.email)) {
    if (vuoto(profile.company)) campi.push("impresa");
    if (vuoto(profile.partita_iva)) campi.push("partita_iva");
    if (vuoto(profile.telefono)) campi.push("telefono");
  }
  return campi;
}
